import React, { useState, useEffect } from 'react';
import {
  Button, Input, Label,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Alert, AlertDescription
} from '../ui';
import { Textarea } from '../ui/textarea';
import EquiposSelector from '../catalogos/EquiposSelector';
import { createEquipo } from '../../lib/supabase';
import { fixMojibake } from '../../lib/utils';

const FASES = [
  { value: 'grupal', label: 'Fase de Grupos' },
  { value: 'octavos', label: 'Octavos de Final' },
  { value: 'cuartos', label: 'Cuartos de Final' },
  { value: 'semifinal', label: 'Semifinal' },
  { value: 'final', label: 'Final' },
  { value: 'tercer_lugar', label: 'Tercer Lugar' },
  { value: 'otro', label: 'Otro' }
];

/**
 * FormJuego - Formulario para crear/editar un juego del torneo
 *
 * @param {object} juego - Juego a editar (null para crear nuevo)
 * @param {object} torneo - Datos del torneo
 * @param {array} gruposTorneo - Grupos del torneo
 * @param {function} onSubmit - Callback al enviar (juegoData)
 * @param {function} onCancel - Callback al cancelar
 * @param {boolean} saving - Estado de guardado
 * @param {boolean} gruposLoading - Si los grupos asignados se estan cargando
 * @param {object} initialData - Datos base para crear un juego similar
 */
const FormJuego = ({
  juego = null,
  torneo,
  gruposTorneo = [],
  onSubmit,
  onCancel,
  saving = false,
  gruposLoading = false,
  lockedGroup = null,
  initialData = null,
  externalError = null
}) => {
  const lockedGroupId = lockedGroup?.id || null;
  const resolvedLockedGroup =
    lockedGroup ||
    (juego
      ? {
          id: juego.grupo_id || juego.grupo?.id || juego.grupos?.id || null,
          nombre_del_grupo:
            juego.grupo?.nombre_del_grupo ||
            juego.grupos?.nombre_del_grupo ||
            juego.grupo?.nombre ||
            ''
        }
      : null);
  const buildInitialState = () => ({
    nombre: '',
    descripcion: '',
    fecha: '',
    hora: '19:00',
    ubicacion: torneo?.sede || '',
    cancha: '',
    campo_numero: '',
    grupo_id: '',
    equipo_id: '',
    equipo_nombre: '',
    fase: 'grupal',
    jornada: null,
    condicion: '',
    comentarios: ''
  });
  const [formData, setFormData] = useState(() => {
    const base = buildInitialState();
    if (!juego && initialData) {
      return { ...base, ...initialData };
    }
    return base;
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (juego) {
      const resolvedGrupoId = juego.grupo_id || juego.grupo?.id || juego.grupos?.id || '';
      setFormData({
        nombre: juego.nombre || '',
        descripcion: juego.descripcion || '',
        fecha: juego.fecha || '',
        hora: juego.hora?.substring(0, 5) || '19:00',
        ubicacion: juego.ubicacion || torneo?.sede || '',
        cancha: juego.cancha || '',
        campo_numero: juego.campo_numero ?? '',
        grupo_id: resolvedGrupoId,
        equipo_id: juego.equipo_id || '',
        equipo_nombre: juego.equipo_nombre || '',
        fase: juego.fase || 'grupal',
        jornada: juego.jornada || null,
        condicion: juego.condicion || '',
        comentarios: juego.comentarios || ''
      });
    }
  }, [juego, torneo]);

  useEffect(() => {
    if (!juego && initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData, juego]);

  useEffect(() => {
    if (!juego && initialData?.grupo_id && !formData.grupo_id) {
      setFormData((prev) => ({ ...prev, grupo_id: initialData.grupo_id }));
    }
  }, [initialData, juego, formData.grupo_id]);

  useEffect(() => {
    if (lockedGroupId) {
      setFormData((prev) => ({ ...prev, grupo_id: lockedGroupId }));
    } else if (!juego && gruposTorneo.length === 1) {
      setFormData((prev) => ({ ...prev, grupo_id: gruposTorneo[0].id }));
    }
  }, [lockedGroupId, gruposTorneo, juego]);

  useEffect(() => {
    if (resolvedLockedGroup?.id && !formData.grupo_id) {
      setFormData((prev) => ({ ...prev, grupo_id: resolvedLockedGroup.id }));
    }
  }, [resolvedLockedGroup, formData.grupo_id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.fecha) {
      setError('La fecha es obligatoria');
      return;
    }

    if (!formData.grupo_id) {
      setError('Debes seleccionar un grupo');
      return;
    }

    if (!formData.equipo_id) {
      setError('Debes seleccionar un equipo rival');
      return;
    }

    // En una liga siempre se sabe si recibes o visitas. Obligatorio en juegos nuevos; al
    // editar uno viejo sin dato, se pide una vez.
    if (formData.condicion !== 'local' && formData.condicion !== 'visita') {
      setError('Indica si tu grupo juega de local o de visita');
      return;
    }

    const juegoData = {
      ...formData,
      torneo_id: torneo.id,
      jornada: formData.jornada ? parseInt(formData.jornada, 10) : null,
      campo_numero: formData.campo_numero ? parseInt(formData.campo_numero, 10) : null
    };

    onSubmit(juegoData);
  };

  const handleCreateEquipo = async (nombreEquipo, nombreCorto = '') => {
    try {
      const equipo = await createEquipo({
        nombre: nombreEquipo,
        nombre_corto: nombreCorto || null,
        activo: true
      });

      setFormData((prev) => ({
        ...prev,
        equipo_id: equipo.id,
        equipo_nombre: equipo.nombre
      }));

      return equipo;
    } catch (err) {
      console.error('Error creating equipo:', err);
      setError('Error al crear el equipo');
      return null;
    }
  };

  // Sin torneo no hay formulario que valga: antes se dereferenciaba torneo.nombre y la
  // pantalla se caia entera. Un componente no derriba la app por una prop vacia.
  if (!torneo) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          No se pudo cargar el torneo de este juego. Cierra y vuelve a elegirlo.
        </AlertDescription>
      </Alert>
    );
  }

  return (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {!error && externalError && (
            <Alert variant="destructive">
              <AlertDescription>{externalError}</AlertDescription>
            </Alert>
          )}

          {/* Información del Torneo */}
          <div className="bg-muted/50 border rounded-lg p-3">
            <p className="text-sm font-medium">{fixMojibake(torneo.nombre)}</p>
            {/* La fecha de fin es OPCIONAL -- una liga en curso no siempre sabe cuando
                termina --, y sin ella esto imprimia "2026-09-01 - " con un guion colgado. */}
            <p className="text-xs text-muted-foreground mt-0.5">
              {torneo.fecha_fin
                ? `${torneo.fecha_inicio} - ${torneo.fecha_fin}`
                : torneo.fecha_inicio || 'Sin fechas'}
            </p>
          </div>

          {/* Nombre del juego (opcional) */}
          <div>
            <Label>
              Nombre del Juego (opcional)
            </Label>
            <Input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              placeholder="Ej: Cuartos de Final vs Águilas"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Si no se especifica, se generará automáticamente
            </p>
          </div>

          {/* Fecha y Hora */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>
                Fecha *
              </Label>
              <Input
                type="date"
                value={formData.fecha}
                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>
                Hora *
              </Label>
              <Input
                type="time"
                value={formData.hora}
                onChange={(e) => setFormData({ ...formData, hora: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Grupo */}
          <div>
            <Label>
              Grupo *
            </Label>
            {resolvedLockedGroup?.id ? (
              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2">
                <p className="text-sm font-semibold text-gray-800">
                  {fixMojibake(resolvedLockedGroup.nombre_del_grupo || resolvedLockedGroup.nombre || 'Grupo asignado')}
                </p>
                <p className="text-xs text-muted-foreground">
                  Este juego se registrará directamente para este grupo.
                </p>
              </div>
            ) : gruposLoading ? (
              <div className="text-sm text-muted-foreground py-2">Cargando grupos asignados...</div>
            ) : gruposTorneo.length === 0 ? (
              <Alert>
                <AlertDescription>
                  Este torneo no tiene grupos asignados. Asigna al menos uno desde la sección
                  "Grupos Participantes" antes de crear juegos.
                </AlertDescription>
              </Alert>
            ) : (
              <Select
                value={formData.grupo_id}
                onValueChange={(value) => setFormData({ ...formData, grupo_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar grupo" />
                </SelectTrigger>
                <SelectContent>
                  {gruposTorneo.map((grupo) => (
                    <SelectItem key={grupo.id} value={grupo.id}>
                      {fixMojibake(grupo.nombre_del_grupo)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Equipo Rival */}
          <div>
            <Label>
              Equipo Rival *
            </Label>
            <EquiposSelector
              value={formData.equipo_id}
              onChange={(equipoId) => setFormData({ ...formData, equipo_id: equipoId })}
              onCreateNew={handleCreateEquipo}
              placeholder="Seleccionar equipo rival"
            />
          </div>

          {/* Local o visita: de que lado juega el grupo del club. El rol y el flyer ponen
              al local a la izquierda; sin este dato el club iba siempre a la izquierda,
              tambien de visita. */}
          <div>
            <Label>¿Tu grupo juega de local o de visita? *</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant={formData.condicion === 'local' ? 'default' : 'outline'}
                aria-pressed={formData.condicion === 'local'}
                onClick={() => setFormData({ ...formData, condicion: 'local' })}
              >
                Local
              </Button>
              <Button
                type="button"
                variant={formData.condicion === 'visita' ? 'default' : 'outline'}
                aria-pressed={formData.condicion === 'visita'}
                onClick={() => setFormData({ ...formData, condicion: 'visita' })}
              >
                Visita
              </Button>
            </div>
          </div>

          {/* Fase y Jornada */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>
                Fase del Torneo
              </Label>
              <Select
                value={formData.fase}
                onValueChange={(value) => setFormData({ ...formData, fase: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FASES.map((fase) => (
                    <SelectItem key={fase.value} value={fase.value}>
                      {fase.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>
                Jornada (opcional)
              </Label>
              <Input
                type="number"
                min="1"
                value={formData.jornada || ''}
                onChange={(e) => setFormData({ ...formData, jornada: e.target.value })}
                placeholder="Ej: 1"
              />
            </div>
          </div>

          {/* Ubicación y Cancha */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>
                Ubicación
              </Label>
              <Input
                type="text"
                value={formData.ubicacion}
                onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })}
                placeholder="Ej: Estadio Municipal"
              />
            </div>

            <div>
              <Label>
                Cancha
              </Label>
              <Input
                type="text"
                value={formData.cancha}
                onChange={(e) => setFormData({ ...formData, cancha: e.target.value })}
                placeholder="Ej: Cancha 1"
              />
            </div>

            <div>
              <Label>
                Número de campo
              </Label>
              <Input
                type="number"
                min="1"
                value={formData.campo_numero ?? ''}
                onChange={(e) => setFormData({ ...formData, campo_numero: e.target.value })}
                placeholder="Ej: 2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Nos ayuda a agrupar el rol de juegos por campo dentro del torneo.
              </p>
            </div>
          </div>

          {/* Descripción */}
          <div>
            <Label>
              Descripción
            </Label>
            <Textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              rows={2}
              placeholder="Detalles adicionales del juego..."
            />
          </div>

          {/* Comentarios */}
          <div>
            <Label>
              Comentarios
            </Label>
            <Textarea
              value={formData.comentarios}
              onChange={(e) => setFormData({ ...formData, comentarios: e.target.value })}
              rows={2}
              placeholder="Notas o comentarios sobre el juego..."
            />
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={saving}
            >
              {saving ? 'Guardando...' : juego ? 'Actualizar' : 'Crear Juego'}
            </Button>
          </div>
        </form>
  );
};

export default FormJuego;


